import type { ReactNode } from "react";
import type { UseQueryResult } from "@tanstack/react-query";

import { apiErrorDetail } from "../api/client";

interface Props<T> {
  query: UseQueryResult<T>;
  children: (data: T) => ReactNode;
  loading?: ReactNode;
  empty?: ReactNode;
  isEmpty?: (data: T) => boolean;
}

/** A failed load as one sentence plus Retry — shared by pages that need it inline. */
export function ErrorStrip({
  message,
  onRetry,
  retrying,
}: {
  message: string;
  onRetry?: () => void;
  retrying?: boolean;
}) {
  return (
    <div role="alert" className="callout callout--danger items-center">
      <span className="flex-1">{message}</span>
      {onRetry ? (
        <button type="button" className="btn btn-ghost btn-sm" disabled={retrying} onClick={onRetry}>
          {retrying ? "Retrying…" : "Retry"}
        </button>
      ) : null}
    </div>
  );
}

function ErrorBox({ query }: { query: UseQueryResult<unknown> }) {
  return (
    <ErrorStrip
      message={apiErrorDetail(query.error) || "Couldn't load this."}
      onRetry={() => void query.refetch()}
      retrying={query.isFetching}
    />
  );
}

export function QueryBoundary<T>({
  query,
  children,
  loading,
  empty,
  isEmpty,
}: Props<T>) {
  if (query.isLoading) {
    return (
      <>
        {loading || (
          <div className="py-10 text-center text-ink-low text-sm">
            <span className="inline-block animate-pulse">Loading…</span>
          </div>
        )}
      </>
    );
  }
  // Only an error with nothing to fall back on replaces the content. A failed
  // *background* refetch must not unmount the subtree: these panels host
  // half-written text (answers, review comments), and swapping them for an
  // error box destroys it. Returning to a backgrounded tab fires a burst of
  // resumed polls, so this is the common case, not the exotic one. Stale data
  // plus a quiet warning beats a blank panel.
  if (query.isError && query.data === undefined) return <ErrorBox query={query} />;
  if (!query.data) return <>{empty || null}</>;

  const body =
    isEmpty && isEmpty(query.data) ? (
      <>{empty || null}</>
    ) : (
      <>{children(query.data)}</>
    );

  if (!query.isError) return body;
  return (
    <>
      <div role="status" className="callout callout--warn mb-3 text-xs">
        Couldn't refresh – showing the last loaded state.
      </div>
      {body}
    </>
  );
}
